import { request } from './client';
import type { RequestOptions } from './client';
import type {
  CandidateFieldDefinition,
  CandidateFieldOptions,
  CandidateFieldType,
  CandidateFieldValue,
} from '@/types/candidate';

const FIELDS_BASE = '/v1/account/candidates/custom-fields';

export interface CandidateFieldDefinitionInput {
  label: string;
  type: CandidateFieldType;
  options?: CandidateFieldOptions | null;
  position?: number;
}

/**
 * Lists the customer's candidate field definitions. Also serves as the field-name autocomplete —
 * pass `search` to prefix-filter by label.
 */
export async function fetchCandidateFields(search?: string): Promise<CandidateFieldDefinition[]> {
  const params: Record<string, string> = {};
  if (search) params.q = search;

  const response = await request('GET', FIELDS_BASE, params);
  return response.data as CandidateFieldDefinition[];
}

export async function createCandidateField(
  body: CandidateFieldDefinitionInput,
  requestOptions: RequestOptions = {}
): Promise<CandidateFieldDefinition> {
  const response = await request('POST', FIELDS_BASE, {}, body, requestOptions);
  return response.data as CandidateFieldDefinition;
}

export async function updateCandidateField(
  fieldKey: string,
  body: Partial<CandidateFieldDefinitionInput>,
  requestOptions: RequestOptions = {}
): Promise<CandidateFieldDefinition> {
  const response = await request('PUT', `${FIELDS_BASE}/${encodeURIComponent(fieldKey)}`, {}, body, requestOptions);
  return response.data as CandidateFieldDefinition;
}

export async function deleteCandidateField(
  fieldKey: string,
  requestOptions: RequestOptions = {}
): Promise<CandidateFieldDefinition> {
  const response = await request('DELETE', `${FIELDS_BASE}/${encodeURIComponent(fieldKey)}`, {}, {}, requestOptions);
  return response.data as CandidateFieldDefinition;
}

/* --------------------------------- VALUES --------------------------------- */

export interface CandidateFieldValueInput {
  field_key?: string;
  label?: string;
  type?: CandidateFieldType;
  options?: CandidateFieldOptions | null;
  value: string | number | null;
}

/**
 * Sets/clears/creates-and-sets candidate field values in one request. An entry with `field_key`
 * updates an existing field (value: null clears it); an entry with `label` + `type` and no
 * `field_key` creates the definition on the fly. Returns the candidate's refreshed custom field
 * values — the page re-renders from this response rather than refetching the whole candidate.
 */
export async function saveCandidateFieldValues(
  candidateEntityId: string,
  values: CandidateFieldValueInput[],
  requestOptions: RequestOptions = {}
): Promise<CandidateFieldValue[]> {
  const response = await request(
    'PUT',
    `/v1/account/candidates/${encodeURIComponent(candidateEntityId)}/custom-fields`,
    {},
    { values },
    requestOptions
  );
  return (response.data?.custom_fields ?? []) as CandidateFieldValue[];
}
